import { Link } from '@tanstack/react-router';
import { m } from 'motion/react';
import { useEffect, useRef } from 'react';

import { useT } from '../../i18n/provider';
import { cn } from '../../lib/cn';

import { CompanySwitcher } from './CompanySwitcher';
import { COMPANY_SECTIONS, isSectionActive } from './sections';
import { SidebarBody } from './Sidebar';

/**
 * La barra cuando estás dentro de una empresa: solo esa empresa.
 *
 * Las veintiuna secciones que antes se apretaban en una fila con scroll
 * horizontal dentro del contenido son ahora la navegación, que es lo que
 * siempre fueron. Lo global no desaparece: vive en el selector de arriba, a un
 * clic, sin robarle sitio a lo que se ha venido a hacer.
 */
export function CompanyNav({
  companySlug,
  companyName,
  pathname,
  onNavigate,
}: {
  companySlug: string;
  companyName: string;
  pathname: string;
  onNavigate: () => void;
}) {
  const t = useT();
  const nav = useRef<HTMLElement>(null);

  /*
    Traer la sección activa a la vista.

    Veintidós secciones no caben en la altura de un portátil, así que entrar por
    un enlace directo a «Zona de peligro» dejaba el resaltado fuera de la parte
    visible: la barra decía, correctamente, que no estabas en ninguna de las que
    se ven. Era el mismo defecto que tenía la fila de pestañas de antes, y no
    valía la pena mudarlo de sitio.

    Se ajusta `scrollTop` a mano en lugar de llamar a `scrollIntoView`, porque
    `scrollIntoView` sube por todos los ancestros desplazables y acaba moviendo
    también la página; aquí lo único que debe moverse es esta columna. Y solo se
    mueve si hace falta: si la sección ya se ve, quieto.
  */
  useEffect(() => {
    const container = nav.current;
    const active = container?.querySelector<HTMLElement>('[aria-current="page"]');

    if (!container || !active) {
      return;
    }

    const top = active.offsetTop - container.offsetTop;
    const bottom = top + active.offsetHeight;

    if (top < container.scrollTop) {
      container.scrollTop = Math.max(0, top - 12);
    } else if (bottom > container.scrollTop + container.clientHeight) {
      container.scrollTop = bottom - container.clientHeight + 12;
    }
  }, [pathname]);

  return (
    <SidebarBody>
      <div className="shrink-0 px-4 pt-4 pb-3">
        <CompanySwitcher companySlug={companySlug} companyName={companyName} onNavigate={onNavigate} />
      </div>

      {/*
        El scroll vive aquí y no en la barra entera: si estuviera arriba, el
        panel del selector —que es `absolute`— se recortaría al abrirse.
      */}
      <nav ref={nav} className="min-h-0 grow overflow-y-auto px-4 pb-4">
        {COMPANY_SECTIONS.map((group) => (
          <div key={group.label} className="mb-5 last:mb-0">
            <p className="px-3 pb-1.5 text-[0.6rem] font-bold tracking-widest text-nav-ink-soft/70 uppercase">
              {t(group.label)}
            </p>

            <div className="flex flex-col gap-0.5">
              {group.sections.map((section) => {
                const active = isSectionActive(section, pathname, companySlug);
                const Icon = section.icon;

                return (
                  <Link
                    key={section.to}
                    to={section.to as '/c/$companySlug'}
                    params={{ companySlug }}
                    onClick={onNavigate}
                    /*
                      El enrutador decide por su cuenta si un enlace es «la
                      página actual» y lo anuncia con `aria-current`, y por
                      defecto le basta con que el camino empiece igual. Sin
                      decírselo, el Panel salía como página actual en las
                      veintiuna secciones —todas cuelgan de su camino—, así que
                      un lector de pantalla anunciaba dos páginas actuales a la
                      vez. Esto le pasa el mismo criterio que usa el resaltado.
                    */
                    activeOptions={{ exact: section.exact }}
                    className={cn(
                      'relative flex min-h-10 items-center gap-3 rounded-sm px-3 text-[0.82rem] transition-colors duration-fast',
                      active ? 'font-semibold text-white' : 'text-nav-ink-soft hover:bg-white/6 hover:text-white',
                    )}
                  >
                    {/*
                      Un único `layoutId` para toda la barra: el resaltado se
                      desliza de una sección a otra en lugar de saltar, que es
                      lo que hace legible de un vistazo de dónde a dónde te has
                      movido.
                    */}
                    {active ? (
                      <m.span
                        layoutId="sidebar-active"
                        className="absolute inset-0 rounded-sm bg-nav-soft shadow-[inset_3px_0_var(--color-primary-bright)]"
                        transition={{ type: 'spring', stiffness: 520, damping: 42 }}
                      />
                    ) : null}
                    <span className="relative [&>svg]:size-4">
                      <Icon />
                    </span>
                    <span className="relative truncate">{t(section.label)}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    </SidebarBody>
  );
}
